import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import styled from "@emotion/styled";
import * as Yup from "yup";
import { Formik } from "formik";

import {
  Alert as MuiAlert,
  Box,
  Button,
  TextField as MuiTextField,
  Typography,
} from "@mui/material";
import { spacing } from "@mui/system";

import { sendSupportRequest } from "../../services/supportService";
import { describeRoadProblem, RoadProblemReport } from "../../utils/support";

const Alert = styled(MuiAlert)(spacing);
const TextField = styled(MuiTextField)<{ my?: number }>(spacing);

type LocationState = {
  roadProblem?: RoadProblemReport;
};

/**
 * Off-screen rather than `display: none`: a bot reading the DOM still sees a
 * plausible field to fill, which is the whole point of a honeypot.
 */
const HoneypotWrapper = styled.div`
  position: absolute;
  left: -5000px;
  width: 1px;
  height: 1px;
  overflow: hidden;
`;

function SupportForm() {
  const location = useLocation();
  const [roadProblem, setRoadProblem] = useState<RoadProblemReport | null>(
    null
  );
  const [sent, setSent] = useState(false);

  // Diagnostics arrive from the register map through router state. Clearing it
  // stops a refresh resurrecting a stale report; the page renders fine without.
  useEffect(() => {
    const state = location.state as LocationState | null;
    if (state?.roadProblem) {
      setRoadProblem(state.roadProblem);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const details = roadProblem ? describeRoadProblem(roadProblem) : [];

  if (sent) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="success">
          Kiitos yhteydenotostasi! Vastaamme antamaasi sähköpostiosoitteeseen.
        </Alert>
        <Button component={Link} to="/register" fullWidth sx={{ mt: 2 }}>
          Takaisin rekisteröitymiseen
        </Button>
      </Box>
    );
  }

  return (
    <Formik
      initialValues={{
        name: "",
        email: "",
        message: "",
        website: "",
        submit: false,
      }}
      validationSchema={Yup.object().shape({
        name: Yup.string().trim().max(255),
        email: Yup.string()
          .trim()
          .email("Virheellinen sähköpostiosoite")
          .max(255)
          .required("Sähköposti on pakollinen"),
        message: Yup.string()
          .trim()
          .max(5000, "Viesti on liian pitkä")
          .required("Viesti on pakollinen"),
      })}
      onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
        try {
          await sendSupportRequest({
            name: values.name.trim() || undefined,
            email: values.email.trim(),
            message: values.message.trim(),
            website: values.website,
            roadProblem: roadProblem ?? undefined,
          });
          setSent(true);
        } catch (error: any) {
          setStatus({ success: false });
          setErrors({
            submit:
              error?.response?.data?.message ||
              "Viestin lähettäminen epäonnistui. Yritä hetken kuluttua uudelleen.",
          });
          setSubmitting(false);
        }
      }}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values,
      }) => (
        <form noValidate onSubmit={handleSubmit}>
          {errors.submit && (
            <Alert mt={2} mb={1} severity="warning">
              {errors.submit}
            </Alert>
          )}

          {details.length > 0 && (
            <Alert mt={2} mb={1} severity="info">
              <Typography variant="body2" gutterBottom>
                Lähetämme viestin mukana nämä tiedot tiestäsi:
              </Typography>
              <Box component="dl" sx={{ m: 0, fontSize: "0.85em" }}>
                {details.map(({ label, value }) => (
                  <Box key={label} sx={{ display: "flex", gap: 1 }}>
                    <Box component="dt" sx={{ fontWeight: 600, flexShrink: 0 }}>
                      {label}:
                    </Box>
                    <Box component="dd" sx={{ m: 0 }}>
                      {value}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Alert>
          )}

          <TextField
            type="text"
            name="name"
            label="Nimi (vapaaehtoinen)"
            value={values.name}
            error={Boolean(touched.name && errors.name)}
            fullWidth
            helperText={touched.name && errors.name}
            onBlur={handleBlur}
            onChange={handleChange}
            my={3}
          />
          <TextField
            type="email"
            name="email"
            label="Sähköposti"
            value={values.email}
            error={Boolean(touched.email && errors.email)}
            fullWidth
            helperText={touched.email && errors.email}
            onBlur={handleBlur}
            onChange={handleChange}
            my={3}
          />
          <TextField
            name="message"
            label="Viesti"
            multiline
            rows={6}
            value={values.message}
            error={Boolean(touched.message && errors.message)}
            fullWidth
            helperText={touched.message && errors.message}
            onBlur={handleBlur}
            onChange={handleChange}
            my={3}
          />

          <HoneypotWrapper aria-hidden="true">
            <label htmlFor="website">Älä täytä tätä kenttää</label>
            <input
              id="website"
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={values.website}
              onChange={handleChange}
            />
          </HoneypotWrapper>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            disabled={isSubmitting}
          >
            Lähetä
          </Button>
          <Button component={Link} to="/register" fullWidth color="primary">
            Takaisin
          </Button>
        </form>
      )}
    </Formik>
  );
}

export default SupportForm;
